import { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { templateApi, runApi } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import GettingStartedGuide from '../components/GettingStartedGuide';
import { formatDateTime } from '../utils/dateFormatter';
import './Dashboard.css';

function Dashboard() {
    const { user } = useAuth();
    const [stats, setStats] = useState({
        templateCount: 0,
        runCount: 0,
        successRate: 0,
        workSaved: 0,
        trustLevel: 100,
        recentActivity: []
    });
    const [loading, setLoading] = useState(true);
    const [showGuide, setShowGuide] = useState(false);

    useEffect(() => {
        // Check if user has seen the guide
        const hasSeenGuide = localStorage.getItem('hasSeenGettingStarted');
        if (!hasSeenGuide) {
            setShowGuide(true);
        }

        const fetchStats = async () => {
            try {
                // Parallel fetch
                const [templatesData, runsData] = await Promise.all([
                    templateApi.list(),
                    runApi.list({ limit: 10 })
                ]);

                const templates = templatesData.templates || [];
                const runs = runsData.runs || [];

                // Simple client-side aggregation
                const successRuns = runs.filter(r => r.status === 'completed' || r.status === 'success').length;
                const successRate = runs.length > 0 ? Math.round((successRuns / runs.length) * 100) : 100;

                // Advanced Metrics
                const workSavedHours = Math.round((runs.length * 15) / 60); // 15 mins saved per run
                const verifiedTemplates = templates.filter(t => t.isActive || t.is_verified).length;
                const trustLevel = templates.length > 0 ? Math.round((verifiedTemplates / templates.length) * 100) : 100;

                setStats({
                    templateCount: templates.length,
                    runCount: runs.length,
                    successRate: successRate,
                    workSaved: workSavedHours,
                    trustLevel: trustLevel,
                    recentActivity: runs
                        .filter(r => r.executedAt || r.executed_at) // Handle both camelCase and snake_case
                        .map(r => ({
                            id: r.id,
                            action: 'Calculation Run',
                            user: r.executedByUsername || r.executed_by_username || 'Unknown User',
                            time: r.executedAt || r.executed_at, // Handle both formats
                            status: r.status,
                            templateName: r.templateName || r.template_name
                        }))
                });

            } catch (err) {
                console.error('Failed to load dashboard stats:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, []);

    const handleCloseGuide = () => {
        setShowGuide(false);
        localStorage.setItem('hasSeenGettingStarted', 'true');
    };

    if (loading) {
        return <LoadingSpinner message="Loading dashboard..." />;
    }

    return (
        <div className="dashboard fade-in">
            {showGuide && <GettingStartedGuide onClose={handleCloseGuide} />}

            <header className="dashboard-header">
                <div>
                    <h1>Welcome, {user?.username}</h1>
                    <p className="text-muted">Here is what is happening in your workspace.</p>
                </div>
                <div className="dashboard-header-actions">
                    <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setShowGuide(true)}
                        title="Show getting started guide"
                    >
                        📖 Getting Started
                    </button>
                    <div className="date-badge">
                        {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                    </div>
                </div>
            </header>

            <div className="stats-grid">
                <div className="stat-card primary">
                    <div className="stat-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2z" />
                            <path d="M19 7h-4a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" />
                        </svg>
                    </div>
                    <div className="stat-content">
                        <h3>Active Templates</h3>
                        <div className="stat-value">{stats.templateCount}</div>
                        <div className="stat-trend positive">Ready for calculation</div>
                    </div>
                </div>

                <div className="stat-card success">
                    <div className="stat-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                        </svg>
                    </div>
                    <div className="stat-content">
                        <h3>Recent Runs</h3>
                        <div className="stat-value">{stats.runCount}</div>
                        <div className="stat-trend positive">In the last period</div>
                    </div>
                </div>

                <div className="stat-card warning">
                    <div className="stat-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                        </svg>
                    </div>
                    <div className="stat-content">
                        <h3>Trust Level</h3>
                        <div className="stat-value">{stats.trustLevel}%</div>
                        <div className="stat-trend positive">Verified Audit Trail</div>
                    </div>
                </div>

                <div className="stat-card info">
                    <div className="stat-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                        </svg>
                    </div>
                    <div className="stat-content">
                        <h3>Manual Work Saved</h3>
                        <div className="stat-value">{stats.workSaved} hrs</div>
                        <div className="stat-trend positive">ROI Generated</div>
                    </div>
                </div>
            </div>

            <div className="dashboard-section">
                <h2>Recent Activity</h2>
                <div className="activity-list">
                    {stats.recentActivity.length === 0 ? (
                        <div className="empty-state">No recent activity found.</div>
                    ) : (
                        stats.recentActivity.map((activity) => (
                            <div key={activity.id} className="activity-item">
                                <div className={`activity-status ${activity.status === 'success' || activity.status === 'completed' ? 'success' : 'error'}`}></div>
                                <div className="activity-details">
                                    <span className="activity-action">{activity.action}</span>
                                    {activity.templateName && (
                                        <span className="activity-meta">{activity.templateName}</span>
                                    )}
                                    <span className="activity-meta">by {activity.user}</span>
                                </div>
                                <div className="activity-time">
                                    {formatDateTime(activity.time, 'Just now')}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            <div className="dashboard-actions">
                <button className="btn btn-primary btn-lg" onClick={() => window.location.href = '/app/portal'}>
                    Go to Calculation Portal
                </button>
                {user.role === 'admin' && (
                    <button className="btn btn-secondary btn-lg" onClick={() => window.location.href = '/app/governance'}>
                        Manage Governance
                    </button>
                )}
            </div>
        </div >
    );
}

export default Dashboard;
